import React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, EmptyState, Skeleton, type BadgeTone } from '@/nova/components';
import { resolveCampaignName, type AdCampaignSummary } from '@/nova/pages/acquisition/api/casino-ad-service';
import type { LumioRow } from '../../lumio-import';
import { Notice } from '../Notice/Notice';
import { HeaderRow, HeaderLeft, HeaderTitle, Count, Panel, AffList } from '../../affiliates.styles';
import { epochSecToYmd } from '../../lumio-import';
import { Row, Thumb, Body, Name, Meta, Actions } from './LumioCampaigns.styles';

export type LumioCampaignsProps = {
  /** Actionable rows only: 'new' (not imported) or 'changed' (source drifted from the import). */
  rows: LumioRow[];
  loading: boolean;
  error?: string;
  onRetry: () => void;
  /** Acquisition campaign_ids whose create/update is in flight. */
  importingIds: ReadonlySet<string>;
  onApply: (row: LumioRow) => void;
};

// Automation states → badge tone (module-level, no i18n). Open union → default neutral.
const STATUS_TONE: Record<string, BadgeTone> = {
  Starting: 'accent',
  Running: 'success',
  Paused: 'neutral',
  Failed: 'danger',
  Deleted: 'neutral',
};

/**
 * Read-only listing of the brand's acquisition (Lumio) campaigns that can be acted on in the
 * affiliate program: import a not-yet-imported one, or update one whose ad-service source has
 * drifted from the imported copy. Imported-and-identical campaigns are omitted upstream. Pure
 * props (no Redux, no network) so the orchestrator owns the data and the write.
 */
export const LumioCampaigns = ({
  rows,
  loading,
  error,
  onRetry,
  importingIds,
  onApply,
}: LumioCampaignsProps): React.ReactElement => {
  const { t } = useTranslation();

  // Literal-keyed so the i18n extractor harvests each label (a dynamic t() would not).
  const statusLabel = (status: string): string => {
    switch (status) {
      case 'Starting':
        return t('Starting');
      case 'Running':
        return t('Running');
      case 'Paused':
        return t('Paused');
      case 'Failed':
        return t('Failed');
      case 'Deleted':
        return t('Deleted');
      default:
        return status;
    }
  };

  const dateLabel = (campaign: AdCampaignSummary): string => {
    const start = epochSecToYmd(campaign.start_date);
    if (campaign.end_date != null) return `${start} → ${epochSecToYmd(campaign.end_date)}`;
    return t('From {{date}}', { date: start });
  };

  let content: React.ReactNode;
  if (error) {
    content = (
      <Notice
        icon="warning"
        title={t('Could not load Lumio campaigns')}
        body={error}
        actionLabel={t('Retry')}
        onAction={onRetry}
      />
    );
  } else if (loading) {
    content = (
      <AffList>
        <Skeleton height={72} block />
        <Skeleton height={72} block />
      </AffList>
    );
  } else if (rows.length === 0) {
    content = <EmptyState text={t('No Lumio campaigns to import')} />;
  } else {
    content = (
      <AffList>
        {rows.map((row) => {
          const { campaign, status } = row;
          const changed = status === 'changed';
          const importing = importingIds.has(campaign.campaign_id);
          return (
            <Row key={campaign.campaign_id}>
              {campaign.master_key_art ? <Thumb src={campaign.master_key_art} alt="" /> : null}
              <Body>
                <Name text={resolveCampaignName(campaign.campaign_name)} tag="span" />
                <Meta text={`${campaign.network} · ${dateLabel(campaign)}`} tag="span" />
              </Body>
              <Actions>
                <Badge label={statusLabel(campaign.status)} tone={STATUS_TONE[campaign.status] ?? 'neutral'} />
                {changed ? <Badge label={t('Changed')} tone="warning" /> : null}
                <Button
                  label={changed ? t('Update') : t('Make available to Affiliates')}
                  variant="primary"
                  icon={changed ? 'refresh' : 'plus'}
                  disabled={importing}
                  onClick={() => onApply(row)}
                />
              </Actions>
            </Row>
          );
        })}
      </AffList>
    );
  }

  return (
    <>
      <HeaderRow>
        <HeaderLeft>
          <HeaderTitle text="Available Lumio campaigns" tag="span" />
          <Count>{loading ? <Skeleton width="2ch" height="0.8em" radius={5} /> : rows.length}</Count>
        </HeaderLeft>
      </HeaderRow>
      <Panel>{content}</Panel>
    </>
  );
};

export default LumioCampaigns;
